import { createContext, useContext, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type TabColor = 'blue' | 'emerald' | 'amber' | 'rose'

const TabsCtx = createContext<{
  value: string
  onChange: (v: string) => void
  color: TabColor
} | null>(null)

interface TabsProps {
  value: string
  onChange: (v: string) => void
  color?: TabColor
  children: ReactNode
  className?: string
}

export function Tabs({ value, onChange, color = 'blue', children, className }: TabsProps) {
  return (
    <TabsCtx.Provider value={{ value, onChange, color }}>
      <div className={cn('flex items-center gap-1 border-b border-slate-200', className)}>
        {children}
      </div>
    </TabsCtx.Provider>
  )
}

interface TabProps {
  value: string
  icon?: ReactNode
  badge?: ReactNode
  children: ReactNode
}

const activeStyles: Record<TabColor, string> = {
  blue: 'border-blue-500',
  emerald: 'border-emerald-500',
  amber: 'border-amber-500',
  rose: 'border-rose-500',
}

export function Tab({ value: v, icon, badge, children }: TabProps) {
  const ctx = useContext(TabsCtx)
  if (!ctx) throw new Error('Tab must be inside Tabs')
  const active = ctx.value === v
  return (
    <button
      onClick={() => ctx.onChange(v)}
      className={cn(
        'px-4 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2',
        active
          ? `text-navy-700 ${activeStyles[ctx.color]}`
          : 'text-slate-500 border-transparent hover:text-navy-700'
      )}
    >
      {icon}
      <span>{children}</span>
      {badge}
    </button>
  )
}
